import { useMemo } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { Link } from 'react-router-dom'

import { AgentRunRow } from '@/components/px/AgentRunRow'
import { ConceptBadge } from '@/components/px/ConceptBadge'
import { AppShell } from '@/components/px/AppShell'
import { DensityNote } from '@/components/px/DensityNote'
import { KpiCard } from '@/components/px/KpiCard'
import { PhaseRibbon } from '@/components/px/PhaseRibbon'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { projectNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ANCHORS,
  DEFECTS_PANEL,
  DEFECT_CAUSES,
  HREFS,
  KPI_LABELS,
  LIBRARY_FEEDBACK,
  NIGHTLY_RUN,
  SUITES,
  SUITE_TABLE,
  TEST_PAGE,
  TOTALS,
  UAT,
  UAT_PLAN,
  UNTESTED,
  UNTESTED_PANEL,
  defectTotal,
  densityNote,
  isHeavyFail,
  rangeLabel,
  suitePass,
  type Suite,
  type SuiteOwner,
} from '@/demo/content/testUat'
import { heroProject, thinSpace } from '@/demo/data'
import { cn } from '@/lib/utils'

/**
 * 3.7 Test — UAT & regressions.
 *
 * The surface is read during BUILD, so it separates two things the deck showed
 * on one slide: the regression estate, which is real now (614 cases generated
 * from 142 requirements, running nightly), and UAT, which has not started
 * because the client has not confirmed the window. The planned window renders
 * as a plan — a 0% bar that says so — rather than borrowing the deck's 38%.
 *
 * Salience is monotonic with severity: the suite carrying seven failures is
 * washed and weighted above every other row, blocked counts carry amber, and
 * the failing figures are never colour alone — each one names its own unit.
 */

const ownerTag = (owner: SuiteOwner): string | null => (owner.kind === 'agent' ? 'agent' : null)

const suiteRowClass = (suite: Suite): string | undefined => {
  if (isHeavyFail(suite)) return 'bg-blocked-bg font-semibold'
  if (suite.fail > 0 || suite.blocked > 0) return 'bg-hairline'
  return undefined
}

export default function TestUatPage() {
  const project = heroProject()
  const nav = useMemo(() => projectNav(project), [project])
  const density = useMemo(() => densityNote(SUITES, TOTALS), [])
  const defects = useMemo(() => defectTotal(DEFECT_CAUSES), [])

  return (
    <AppShell
      moduleLabel={TEST_PAGE.moduleLabel}
      breadcrumb={project.name}
      contextPill={TEST_PAGE.contextPill}
      nav={nav}
    >
      <header className="max-w-4xl">
        <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-serif text-2xl font-semibold text-foreground">{TEST_PAGE.title}</h1>
          <ConceptBadge />
        </div>
        <p className="mt-1.5 text-sm text-ink-soft">{TEST_PAGE.standfirst}</p>
      </header>

      <PhaseRibbon current={TEST_PAGE.phase} className="mt-4" />

      <p className="mt-2 text-xs text-muted-foreground">
        {TEST_PAGE.phaseNote}{' '}
        <Link to={HREFS.requirements} className="font-medium text-accent-foreground hover:underline">
          {TEST_PAGE.traceLinkLabel}
        </Link>
        {' · '}
        <Link to={HREFS.migration} className="font-medium text-accent-foreground hover:underline">
          {TEST_PAGE.migrationLinkLabel}
        </Link>
      </p>

      {/* ── Figures ─────────────────────────────────────────────────────── */}
      <section className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Test figures">
        <KpiCard
          label={KPI_LABELS.cases}
          figure={thinSpace(TOTALS.cases)}
          qualifier={`generated from ${TOTALS.covered + TOTALS.untested} requirements`}
        />
        <KpiCard
          label={KPI_LABELS.coverage}
          figure={`${TOTALS.coveragePct}%`}
          qualifier={
            <>
              {SUITE_TABLE.untestedLabel(TOTALS.untested)} ·{' '}
              <a href={`#${ANCHORS.untested}`} className="font-medium text-accent-foreground hover:underline">
                which ones
              </a>
            </>
          }
        />
        <KpiCard
          label={KPI_LABELS.passing}
          figure={thinSpace(TOTALS.pass)}
          qualifier={
            <>
              <span className="font-semibold text-rose-ink">{SUITE_TABLE.failLabel(TOTALS.fail)}</span>
              {' · '}
              <span className="font-medium text-review">{SUITE_TABLE.blockedLabel(TOTALS.blocked)}</span>
            </>
          }
        />
        <KpiCard
          label={UAT.kpi.label}
          figure={
            <span className="flex items-center gap-2">
              {UAT.startLabel}
              <StatusPill tone="blocked">{UAT.lateLabel}</StatusPill>
            </span>
          }
          qualifier={
            <>
              {UAT.kpi.qualifier} ·{' '}
              <Link to={HREFS.portal} className="font-medium text-accent-foreground hover:underline">
                {UAT.portalLinkLabel}
              </Link>
            </>
          }
        />
      </section>

      {/* ── Overnight run ───────────────────────────────────────────────── */}
      <section aria-labelledby="nightly-heading" className="mt-7 rounded-lg border border-border bg-card px-4 py-3">
        <SectionLabel>{NIGHTLY_RUN.panelLabel}</SectionLabel>
        <h2 id="nightly-heading" className="sr-only">
          {NIGHTLY_RUN.panelLabel}
        </h2>
        <AgentRunRow
          tone={NIGHTLY_RUN.tone}
          label={NIGHTLY_RUN.pillLabel}
          text={NIGHTLY_RUN.text}
          time={NIGHTLY_RUN.time}
          runHref={NIGHTLY_RUN.runHref}
        />
        <p className="border-t border-border pt-2.5 text-xs text-muted-foreground">
          <Link to={NIGHTLY_RUN.agentHref} className="font-medium text-accent-foreground hover:underline">
            {NIGHTLY_RUN.agentName}
          </Link>{' '}
          — {NIGHTLY_RUN.agentLine} {NIGHTLY_RUN.landedLine}
        </p>
      </section>

      {/* ── Suites ──────────────────────────────────────────────────────── */}
      <section
        id={ANCHORS.suites}
        aria-labelledby="suites-heading"
        className="mt-6 rounded-lg border border-border bg-card"
      >
        <div className="border-b border-border px-4 py-3">
          <SectionLabel>{SUITE_TABLE.label}</SectionLabel>
          <h2 id="suites-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
            {SUITE_TABLE.heading}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">{SUITE_TABLE.caption}</p>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{SUITE_TABLE.columns.suite}</TableHead>
              <TableHead>{SUITE_TABLE.columns.requirements}</TableHead>
              <TableHead className="text-right">{SUITE_TABLE.columns.cases}</TableHead>
              <TableHead className="text-right">{SUITE_TABLE.columns.pass}</TableHead>
              <TableHead className="text-right">{SUITE_TABLE.columns.fail}</TableHead>
              <TableHead>{SUITE_TABLE.columns.owner}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {SUITES.map((suite) => (
              <TableRow key={suite.key} className={cn(suiteRowClass(suite))}>
                <TableCell className="px-4">
                  <span className="flex flex-col leading-tight">
                    <span className="text-[13px] font-medium text-foreground">{suite.name}</span>
                    <span className="text-xs text-muted-foreground">{suite.epic}</span>
                  </span>
                </TableCell>
                <TableCell>
                  <span className="flex flex-col items-start leading-tight">
                    <Link
                      to={HREFS.requirements}
                      title={SUITE_TABLE.rangeLinkTitle}
                      className="v-figure font-mono text-[13px] font-medium text-accent-foreground hover:underline"
                    >
                      {rangeLabel(suite)}
                    </Link>
                    <span className="text-xs text-muted-foreground">
                      {suite.covered} covered
                      {suite.untested > 0 && (
                        <>
                          {' · '}
                          <a href={`#${ANCHORS.untested}`} className="font-medium text-review hover:underline">
                            {SUITE_TABLE.untestedLabel(suite.untested)}
                          </a>
                        </>
                      )}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="v-figure text-right tabular-nums text-foreground">{suite.cases}</TableCell>
                <TableCell className="v-figure text-right tabular-nums text-foreground">
                  {suitePass(suite)}
                </TableCell>
                <TableCell className="text-right">
                  <span className="flex flex-col items-end leading-tight">
                    <span
                      className={cn(
                        'v-figure tabular-nums',
                        suite.fail === 0 ? 'text-muted-foreground' : 'font-semibold text-rose-ink',
                        isHeavyFail(suite) && 'text-base'
                      )}
                    >
                      {suite.fail}
                      <span className="sr-only"> {SUITE_TABLE.failLabel(suite.fail)}</span>
                    </span>
                    {suite.blocked > 0 && (
                      <span className="text-xs font-medium text-review">
                        {SUITE_TABLE.blockedLabel(suite.blocked)}
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="px-4">
                  <span className="flex flex-col leading-tight">
                    <span className="flex items-center gap-1.5">
                      <Link
                        to={suite.owner.href}
                        className="text-[13px] font-medium text-accent-foreground hover:underline"
                      >
                        {suite.owner.name}
                      </Link>
                      {ownerTag(suite.owner) && (
                        <Badge variant="outline" className="text-[10px] font-medium">
                          {ownerTag(suite.owner)}
                        </Badge>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground">{suite.owner.meta}</span>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ul className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          {SUITES.filter((suite) => suite.note !== null).map((suite) => (
            <li key={suite.key} className="mt-0.5 first:mt-0">
              <span className={cn('font-medium', isHeavyFail(suite) ? 'text-rose-ink' : 'text-foreground')}>
                {suite.name}
              </span>{' '}
              — {suite.note}
            </li>
          ))}
          <li className="mt-1.5">{SUITE_TABLE.agentOwnerNote}</li>
          <li className="mt-0.5">{SUITE_TABLE.qaOwnerNote}</li>
        </ul>
      </section>

      {/* ── Defects by cause ────────────────────────────────────────────── */}
      <section
        id={ANCHORS.defects}
        aria-labelledby="defects-heading"
        className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
      >
        <SectionLabel>{DEFECTS_PANEL.label}</SectionLabel>
        <h2 id="defects-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {DEFECTS_PANEL.heading}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{DEFECTS_PANEL.lead(defects)}</p>

        <dl className="mt-4 grid gap-3 sm:grid-cols-2">
          {DEFECT_CAUSES.map((cause) => (
            <div key={cause.key} className="rounded-md border border-border px-3 py-2.5">
              <dt className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-semibold text-foreground">{cause.label}</span>
                <span className="v-figure font-serif text-[20px] font-semibold tabular-nums text-rose-ink">
                  {cause.count}
                  <span className="sr-only"> {DEFECTS_PANEL.countUnit(cause.count)}</span>
                </span>
              </dt>
              <dd className="mt-1 text-xs text-muted-foreground">
                {cause.prose}
                {cause.href && cause.linkLabel && (
                  <>
                    {' '}
                    <Link to={cause.href} className="font-medium text-accent-foreground hover:underline">
                      {cause.linkLabel} →
                    </Link>
                  </>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ── The untested requirements ───────────────────────────────────── */}
      <section
        id={ANCHORS.untested}
        aria-labelledby="untested-heading"
        className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
      >
        <SectionLabel>{UNTESTED_PANEL.label}</SectionLabel>
        <h2 id="untested-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {UNTESTED_PANEL.heading(TOTALS.untested)}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {UNTESTED_PANEL.lead(TOTALS.untested, TOTALS.covered + TOTALS.untested, UNTESTED_PANEL.openRequirements)}
        </p>

        <Table className="mt-3">
          <TableHeader>
            <TableRow>
              <TableHead>{UNTESTED_PANEL.columns.requirement}</TableHead>
              <TableHead>{UNTESTED_PANEL.columns.epic}</TableHead>
              <TableHead>{UNTESTED_PANEL.columns.reason}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {UNTESTED.map((requirement) => (
              <TableRow key={requirement.key}>
                <TableCell>
                  <span className="flex items-baseline gap-2">
                    <span className="v-figure font-mono text-[13px] text-muted-foreground">
                      {requirement.idLabel}
                    </span>
                    <span className="whitespace-normal text-[13px] font-medium text-foreground">
                      {requirement.title}
                    </span>
                  </span>
                </TableCell>
                <TableCell className="text-[13px] text-ink-soft">{requirement.epic}</TableCell>
                <TableCell className="whitespace-normal text-[13px] text-review">{requirement.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <p className="mt-2.5 text-xs text-muted-foreground">
          <Link to={HREFS.requirements} className="font-medium text-accent-foreground hover:underline">
            {UNTESTED_PANEL.registerLinkLabel} →
          </Link>
        </p>
      </section>

      {/* ── UAT window · planned ────────────────────────────────────────── */}
      <section
        id={ANCHORS.uatWindow}
        aria-labelledby="uat-heading"
        className="mt-6 rounded-lg border border-border bg-card px-4 py-4"
      >
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{UAT.panelLabel}</SectionLabel>
          <StatusPill tone="blocked">{UAT.lateLabel}</StatusPill>
          <span className="text-xs text-muted-foreground">{UAT.dueLabel}</span>
        </div>
        <h2 id="uat-heading" className="mt-0.5 font-serif text-lg font-semibold text-foreground">
          {UAT.heading}
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {UAT.blockingLine}{' '}
          <Link to={HREFS.portal} className="font-medium text-accent-foreground hover:underline">
            {UAT.portalLinkLabel}
            <ArrowUpRight className="ml-0.5 inline h-3 w-3" aria-hidden />
          </Link>
        </p>

        <dl className="mt-4 grid gap-2 sm:grid-cols-3">
          {UAT_PLAN.map((line) => (
            <div key={line.key} className="rounded-md border border-border px-3 py-2">
              <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {line.label}
              </dt>
              <dd className="mt-0.5 text-[13px] text-foreground">{line.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-4 max-w-md">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs font-medium text-foreground">{UAT.progressLabel}</span>
            <span className="v-figure text-xs tabular-nums text-muted-foreground">{UAT.progressReading}</span>
          </div>
          <Progress
            value={UAT.progressPct}
            aria-label={`${UAT.progressLabel} — ${UAT.progressReading}`}
            className="mt-1.5"
          />
          <p className="mt-1.5 text-xs text-muted-foreground">{UAT.progressNote}</p>
        </div>
      </section>

      {/* ── Back into the library (mint = what PX proposes) ──────────────── */}
      <section aria-labelledby="library-heading" className="mt-6 rounded-lg border border-border bg-accent px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <SectionLabel>{LIBRARY_FEEDBACK.label}</SectionLabel>
          <StatusPill tone="proposed">{LIBRARY_FEEDBACK.tag}</StatusPill>
        </div>
        <h2 id="library-heading" className="mt-2 max-w-3xl font-serif text-lg font-semibold text-foreground">
          {LIBRARY_FEEDBACK.headlineBefore}
          <Link to={LIBRARY_FEEDBACK.packHref} className="text-accent-foreground underline">
            {LIBRARY_FEEDBACK.packLabel}
          </Link>
          {LIBRARY_FEEDBACK.headlineAfter}
        </h2>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground">{LIBRARY_FEEDBACK.prose}</p>
        <p className="mt-2 max-w-3xl text-xs text-muted-foreground">
          {LIBRARY_FEEDBACK.gateLine} {LIBRARY_FEEDBACK.countNote}{' '}
          <a href={LIBRARY_FEEDBACK.failuresHref} className="font-medium text-accent-foreground hover:underline">
            {LIBRARY_FEEDBACK.failuresLinkLabel} →
          </a>
        </p>
      </section>

      <DensityNote>{density}</DensityNote>
    </AppShell>
  )
}
